import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../slices/types';
import { fetchBankAccounts, reviewBankAccount } from '../../slices/bank/bankAccountsSlice';
import { Banknote, CheckCircle2, XCircle, Shield, User, Hash } from 'lucide-react';

const BankDetails: React.FC = () => {
  const dispatch = useAppDispatch();
  const { items, loading, error } = useAppSelector((s) => s.BankAccounts);

  useEffect(() => {
    dispatch(fetchBankAccounts());
  }, [dispatch]);

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Bank Details</h1>
      </div>

      {loading && (
        <div className="text-gray-600">Loading bank accounts...</div>
      )}
      {error && (
        <div className="text-red-600">{error}</div>
      )}

      <div className="space-y-4">
        {items.map(acc => (
          <div key={acc.id} className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Banknote className="text-primary w-6 h-6" />
                </div>
                <div className="ml-4">
                  <div className="text-sm text-gray-500">Account Holder</div>
                  <div className="text-lg font-semibold text-gray-900">{acc.account_holder_name}</div>
                </div>
              </div>
              {acc.status === 'verified' ? (
                <span className="inline-flex items-center px-3 py-1 text-sm font-semibold rounded-full bg-green-100 text-green-800">
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Verified
                </span>
              ) : (
                <span className="inline-flex items-center px-3 py-1 text-sm font-semibold rounded-full bg-gray-100 text-gray-800">
                  <Shield className="w-4 h-4 mr-1" /> {acc.status}
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <div className="space-y-2">
                <div className="text-gray-500 text-xs uppercase tracking-wide">Bank Details</div>
                <div>
                  <span className="text-gray-500">Bank:</span>
                  <span className="ml-2 font-medium text-gray-900">{acc.bank_name}</span>
                </div>
                <div>
                  <span className="text-gray-500">Branch:</span>
                  <span className="ml-2 font-medium text-gray-900">{acc.branch_name}</span>
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-gray-500 text-xs uppercase tracking-wide">Account Info</div>
                <div>
                  <span className="text-gray-500">Account Number:</span>
                  <span className="ml-2 font-mono text-gray-900">{acc.account_number}</span>
                </div>
                <div>
                  <span className="text-gray-500">IFSC Code:</span>
                  <span className="ml-2 font-mono text-gray-900">{acc.ifsc_code}</span>
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-gray-500 text-xs uppercase tracking-wide">Account Type</div>
                <div>
                  <span className="text-gray-500">Type:</span>
                  <span className="ml-2 font-medium text-gray-900 capitalize">{acc.account_type}</span>
                </div>
                <div>
                  <span className="text-gray-500">Default:</span>
                  <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${acc.is_default ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {acc.is_default ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-gray-500 text-xs uppercase tracking-wide">Timestamps</div>
                <div>
                  <span className="text-gray-500">Created:</span>
                  <span className="ml-2 text-gray-900">{new Date(acc.created_at).toLocaleDateString()}</span>
                </div>
                <div>
                  <span className="text-gray-500">Updated:</span>
                  <span className="ml-2 text-gray-900">{new Date(acc.updated_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            {acc.verification_notes && (
              <div className="mt-4 p-3 bg-gray-50 rounded text-sm text-gray-600">
                <div className="text-gray-500 text-xs uppercase tracking-wide mb-1">Verification Notes</div>
                {acc.verification_notes}
              </div>
            )}

            {/* Action Row */}
            <div className="mt-4 flex items-center justify-end">
              {acc.status === 'verified' ? (
                <span className="text-sm text-gray-500">No Action</span>
              ) : (
                <div className="flex space-x-2">
                  <button
                    type="button"
                    className="bg-blue-600 hover:bg-orange-700 text-white px-5 py-2 rounded text-xs"
                    onClick={() => dispatch(reviewBankAccount({ id: acc.id, payload: { status: 'verified', verification_notes: 'Bank account verified successfully' } }))}
                  >
                    Verified
                  </button>
                  <button
                    type="button"
                    className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-5 py-2 rounded text-xs"
                    onClick={() => dispatch(reviewBankAccount({ id: acc.id, payload: { status: 'rejected', rejection_reason: 'Invalid bank details', admin_notes: 'Please verify your bank account details' } }))}
                  >
                    Rejected
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BankDetails;


